import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFormMutations } from "@/hooks/use-form-mutations";
import type { FormWithFields, FieldProperties } from "@/types/forms";

const CHOICE_FIELD_TYPES = ["dropdown", "checkbox", "emoji_scale"];

const typeLabels: Record<string, string> = {
  manifest_sod: "Start of Day",
  manifest_eod: "End of Day",
  standard: "Standard",
};

interface FormHeaderProps {
  form: FormWithFields;
}

export function FormHeader({ form }: FormHeaderProps) {
  const { orgId, slug } = useParams<{ orgId: string; slug: string }>();
  const navigate = useNavigate();
  const { update, setStatus } = useFormMutations();
  const [title, setTitle] = useState(form.title);
  const [description, setDescription] = useState(form.description ?? "");

  async function handleTitleBlur() {
    if (title.trim() && title !== form.title) {
      try {
        await update.mutateAsync({ id: form.id, data: { title } });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to update title");
        setTitle(form.title);
      }
    }
  }

  async function handleDescriptionBlur() {
    const next = description.trim() || null;
    if (next !== (form.description ?? null)) {
      try {
        await update.mutateAsync({ id: form.id, data: { description: next } });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to update description");
        setDescription(form.description ?? "");
      }
    }
  }

  function validateBeforePublish(): string | null {
    if (form.fields.length === 0) return "Add at least one field";

    const unlabeled = form.fields.find((f) => !f.label.trim());
    if (unlabeled) return "All fields must have a label";

    for (const field of form.fields) {
      if (CHOICE_FIELD_TYPES.includes(field.type) && field.properties) {
        try {
          const props = JSON.parse(field.properties) as FieldProperties;
          const items = props.options ?? props.emojis ?? [];
          if (items.length === 0) {
            return `"${field.label || "Untitled field"}" needs at least one option`;
          }
        } catch {
          // Invalid JSON — skip, backend will catch it
        }
      }
    }
    return null;
  }

  async function handlePublish() {
    const error = validateBeforePublish();
    if (error) {
      toast.error(error);
      return;
    }
    try {
      await setStatus.mutateAsync({ id: form.id, status: "published" });
      toast.success("Form published");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to publish form");
    }
  }

  async function handleUnpublish() {
    try {
      await setStatus.mutateAsync({ id: form.id, status: "draft" });
      toast.success("Form unpublished");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to unpublish form");
    }
  }

  async function handleArchive() {
    try {
      await setStatus.mutateAsync({ id: form.id, isArchived: true });
      toast.success("Form archived");
      navigate(`/${orgId}/${slug}/settings/forms`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to archive form");
    }
  }

  async function handleRestore() {
    try {
      await setStatus.mutateAsync({ id: form.id, isArchived: false });
      toast.success("Form restored");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to restore form");
    }
  }

  const isDraft = form.status === "draft";
  const isPublished = form.status === "published";
  const hasFields = form.fields.length > 0;

  return (
    <div className="space-y-3">
      {/* Top bar: back link + badges + actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/${orgId}/${slug}/settings/forms`)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Forms
        </button>

        <div className="flex items-center gap-2">
          <Badge variant="secondary">{typeLabels[form.type] ?? form.type}</Badge>
          <Badge variant={isPublished ? "default" : "outline"}>
            {form.status}
          </Badge>
          {form.isArchived && (
            <Badge variant="outline">archived</Badge>
          )}

          {isDraft && !form.isArchived && hasFields && (
            <Button size="sm" onClick={handlePublish} disabled={setStatus.isPending}>
              {setStatus.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publish
            </Button>
          )}

          {isDraft && !form.isArchived && !hasFields && (
            <Button size="sm" disabled title="Add at least one field">
              Publish
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isPublished && !form.isArchived && (
                <DropdownMenuItem
                  onClick={handleUnpublish}
                  disabled={setStatus.isPending}
                >
                  Unpublish
                </DropdownMenuItem>
              )}
              {form.isArchived && (
                <DropdownMenuItem
                  onClick={handleRestore}
                  disabled={setStatus.isPending}
                >
                  Restore
                </DropdownMenuItem>
              )}
              {!form.isArchived && (
                <DropdownMenuItem
                  onClick={handleArchive}
                  disabled={setStatus.isPending}
                  className="text-destructive focus:text-destructive"
                >
                  Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Title */}
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={handleTitleBlur}
        placeholder="Untitled form"
        className="text-lg font-semibold h-auto border-transparent px-2 shadow-none hover:border-input focus-visible:border-input focus-visible:ring-1 transition-colors"
      />

      {/* Description */}
      <Input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={handleDescriptionBlur}
        placeholder="Add a description..."
        className="h-auto border-transparent px-2 text-sm text-muted-foreground shadow-none hover:border-input focus-visible:border-input focus-visible:ring-1 transition-colors"
      />
    </div>
  );
}
